import React, { useState } from 'react';
import { Controller } from 'react-hook-form-new';
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Link,
  List,
  ListItem,
  RadioGroup,
  Stack,
  Typography,
} from '@nextiva/next-ui-components';
import {
  ICompanyTypeInfo,
  RadioGroupCardCompanyTypeProps,
} from 'interface/WizardV3';
import { BRAND_REGISTRATION_FIELDS, CompanyTypeInfo } from '../constants';
import { useWizardContextV3 } from '../WizardContextV3/WizardContextV3';
import { SolePropDialog } from '../CustomDialog/CustomDialog';
import { LinkStyled } from '../../RequestWebsiteCard/RequestWebsiteCard.styled';
import { WebsiteStatusDialog } from '../../RequestWebsiteCard/WebsiteStatusDialog/WebsiteStatusDialog';
import { RequestWebsiteForm } from '../../RequestWebsiteCard/RequestWebsiteForm/RequestWebsiteForm';
import { CardCompanyType } from './CardCompanyType';
import { ListItemTextStyled } from './CardCompanyType.styled';

const dataTestId = 'PlatformAdminUI-CompanyType';
function RadioGroupCardCompanyType({ value }: RadioGroupCardCompanyTypeProps) {
  return (
    <Grid container columnSpacing={2} rowSpacing={0} columns={10}>
      {CompanyTypeInfo.map((companyInfo: ICompanyTypeInfo) => (
        <Grid item xs={2} key={companyInfo.value}>
          <FormControlLabel
            value={companyInfo.value}
            label=""
            control={
              <CardCompanyType
                key={companyInfo.value}
                companyInfo={companyInfo}
                isSelected={companyInfo.value === value}
                dataTestId={dataTestId}
              />
            }
          />
        </Grid>
      ))}
    </Grid>
  );
}

export function CompanyType() {
  const { getValues, watch, control, handleNextStep } = useWizardContextV3();

  const [openDialog, setOpenDialog] = useState(false);
  const [openViewStatus, setOpenViewStatus] = useState(false);
  const [openRequestWebsite, setOpenRequestWebsite] = useState(false);
  const [isSoleProprietor, setIsSoleProprietor] = useState(
    getValues(BRAND_REGISTRATION_FIELDS.COMPANY_TYPE) ===
      CompanyTypeInfo[4].value,
  );
  const isCompanyTypeSelected = !!watch(BRAND_REGISTRATION_FIELDS.COMPANY_TYPE);
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };
  const handleCancel = () => {
    setOpenDialog(false);
  };
  const handleAccept = () => {
    handleCancel();
    handleNextStep();
  };

  return (
    <Stack
      alignItems="center"
      direction="column"
      spacing={4}
      data-testid={dataTestId}
      sx={{ padding: '52px 64px 40px 64px' }}
    >
      <Box>
        <Typography
          variant="h5"
          align="center"
          color="#0B1029"
          data-testid={`${dataTestId}-Header`}
        >
          Select your company type
        </Typography>
        <Typography
          variant="body2"
          align="center"
          color="#55596B"
          data-testid={`${dataTestId}-Subheader`}
        >
          Select only one
        </Typography>
      </Box>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Controller
          control={control}
          name={BRAND_REGISTRATION_FIELDS.COMPANY_TYPE}
          rules={{ required: true }}
          render={({ field: { value, onChange, onBlur } }) => (
            <RadioGroup
              data-testid={`${dataTestId}-RadioGroup`}
              name="radio-buttons-company-type"
              value={value || ''}
              onBlur={onBlur}
              onChange={(e) => {
                onChange(e.target.value);
                setIsSoleProprietor(e.target.value === 'SOLE_PROPRIETOR');
              }}
            >
              <RadioGroupCardCompanyType value={value} />
            </RadioGroup>
          )}
        />
      </Stack>
      <Box>
        <Box
          padding="16px 24px"
          sx={{ backgroundColor: '#F7F8FA', maxWidth: '567px' }}
          data-testid={`${dataTestId}-InfoBox`}
        >
          <Typography variant="caption" align="left" color="#0B1029">
            Please keep the following information ready with you before you
            start registration:
          </Typography>
          <List
            disablePadding
            sx={{ listStyle: 'disc', pl: 2, color: '#0B1029' }}
            data-testid={`${dataTestId}-List`}
          >
            <ListItem disablePadding sx={{ display: 'list-item' }}>
              <ListItemTextStyled
                primaryTypographyProps={{
                  variant: 'caption',
                  color: '#0B1029',
                }}
              >
                Required information described in company type.
              </ListItemTextStyled>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'list-item' }}>
              <ListItemTextStyled
                primaryTypographyProps={{
                  variant: 'caption',
                  color: '#0B1029',
                }}
              >
                Business address.
              </ListItemTextStyled>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'list-item' }}>
              <ListItemTextStyled
                primaryTypographyProps={{
                  variant: 'caption',
                  color: '#0B1029',
                }}
              >
                Link of working website or social media page.
              </ListItemTextStyled>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'list-item' }}>
              <ListItemTextStyled
                primaryTypographyProps={{
                  variant: 'caption',
                  color: '#0B1029',
                }}
              >
                Link/screenshot/document of customer consent form that includes
                mandatory disclosures.
              </ListItemTextStyled>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'list-item' }}>
              <ListItemTextStyled
                primaryTypographyProps={{
                  variant: 'caption',
                  color: '#0B1029',
                }}
              >
                Explanation of usage such as who the target recipients are, what
                are the messaging topics, etc.
              </ListItemTextStyled>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'list-item' }}>
              <ListItemTextStyled
                primaryTypographyProps={{
                  variant: 'caption',
                  color: '#0B1029',
                }}
              >
                Script/documents of sample messages you’re planning to send.
              </ListItemTextStyled>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'list-item' }}>
              <ListItemTextStyled
                primaryTypographyProps={{
                  variant: 'caption',
                  color: '#0B1029',
                }}
              >
                Link/document of privacy policy.
              </ListItemTextStyled>
            </ListItem>
            <ListItem disablePadding sx={{ display: 'list-item' }}>
              <ListItemTextStyled
                primaryTypographyProps={{
                  variant: 'caption',
                  color: '#0B1029',
                }}
              >
                Business website. (
                <LinkStyled
                  onClick={() => setOpenRequestWebsite(true)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${dataTestId}-CompanyType-SendEmail`}
                >
                  <Typography variant="caption" fontWeight="bold">
                    Send request
                  </Typography>
                </LinkStyled>{' '}
                to get a website.{' '}
                <LinkStyled
                  onClick={() => setOpenViewStatus(true)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${dataTestId}-CompanyType-SendEmail`}
                >
                  <Typography variant="caption" fontWeight="bold">
                    Check status
                  </Typography>
                </LinkStyled>{' '}
                if already requested)
              </ListItemTextStyled>
            </ListItem>
          </List>
          <Link
            variant="caption"
            href="https://www.nextiva.com/support/articles/sms-faqs.html"
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${dataTestId}-InfoBox-LearnMore`}
            sx={{ fontWeight: 400 }}
          >
            <br />
            Learn more.
          </Link>
        </Box>
      </Box>
      <Box>
        <Button
          data-testid={`${dataTestId}-StartRegistrationButton`}
          disabled={!isCompanyTypeSelected}
          onClick={isSoleProprietor ? handleOpenDialog : handleNextStep}
          variant="contained"
        >
          Start registration
        </Button>
        <SolePropDialog
          handleCancel={handleCancel}
          handleAccept={handleAccept}
          openDialog={openDialog}
        />
      </Box>
      <RequestWebsiteForm
        open={openRequestWebsite}
        handleClose={() => setOpenRequestWebsite(false)}
      />
      <WebsiteStatusDialog
        handleDone={() => setOpenViewStatus(false)}
        handleOpenRequestWebsite={() => {
          setOpenRequestWebsite(true);
          setOpenViewStatus(false);
        }}
        open={openViewStatus}
      />
    </Stack>
  );
}
